/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, getAuthClient } from '../firebase';
import { logger } from '../utils/dataIsolationLogger';
import { logBootEvent } from '../init/bootLogger';
import { canonicalizeVestigingId, resolveVestigingDocId } from '../utils/resolveVestigingId';
import { buildRoleCapabilities, deriveRoleFlags } from '../utils/userRoles';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../features/app/appConfig';
import { normalizeNotificationPreferences } from '../features/app/appNotificationHelpers';

function resolveAuthClient() {
  return typeof getAuthClient === 'function' ? getAuthClient() : auth;
}

export default function useAppAuthContext() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState('initializing'); // initializing | authenticated | unauthenticated | timeout | error
  const [userDocLoading, setUserDocLoading] = useState(false);
  const [userDocError, setUserDocError] = useState(null);
  const [profileStatus, setProfileStatus] = useState('idle'); // idle | loading | approved | unapproved | missing | error
  const [branchAccessResolved, setBranchAccessResolved] = useState(false);
  const [approved, setApproved] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [userBranchId, setUserBranchId] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationPreferences, setNotificationPreferences] = useState(() => ({
    ...DEFAULT_NOTIFICATION_PREFERENCES,
  }));
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [approvalResolved, setApprovalResolved] = useState(false);
  const [userRoleCapabilities, setUserRoleCapabilities] = useState(() => buildRoleCapabilities({}));
  const [demoMode, setDemoMode] = useState(() => ({ enabled: false, showroom: false }));
  const [userProfile, setUserProfile] = useState({});
  const authProfileSignatureRef = useRef(null);
  const firstUserSnapshotLoggedRef = useRef(false);
  const lastLoggedBranchRef = useRef(null);

  useEffect(() => {
    const authClient = resolveAuthClient();
    // register auth state change with safety timeout
    let unsubscribe = () => {};
    const initTimeout = setTimeout(() => {
      try { console.warn('Auth init timeout — auth init timed out'); } catch { void 0; }
      // Mark as timed out — do not treat as "ready/unauthenticated"
      setAuthLoading(false);
      setAuthStatus('timeout');
    }, Number(process.env.REACT_APP_AUTH_INIT_TIMEOUT_MS || 8000));
    try {
      unsubscribe = onAuthStateChanged(authClient, async (currentUser) => {
      try { logBootEvent('currentUser', currentUser ? currentUser.uid : 'null'); } catch { void 0; }
      if (currentUser) {
        setAuthStatus('authenticated');
        setUser(currentUser);
        setApprovalResolved(false);
        setApproved(null);
        logger.info('AUTH', `✅ User authenticated: ${currentUser.email}`);

        // Persist login metadata once per auth session (auth_time changes only on re-login).
        try {
            const tokenResult = authClient && authClient.currentUser ? await authClient.currentUser.getIdTokenResult() : null;
          const authTimeSec = Number(tokenResult?.claims?.auth_time || 0);
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          const currentData = userSnap.exists() ? (userSnap.data() || {}) : {};
          const knownAuthTimeSec = Number(currentData?.lastAuthTimeSec || 0);

          if (!knownAuthTimeSec || (authTimeSec && authTimeSec !== knownAuthTimeSec)) {
            await setDoc(userRef, {
              uid: currentUser.uid,
              email: currentUser.email || null,
              previousLoginAt: currentData?.lastLoginAt || currentData?.previousLoginAt || null,
              lastLoginAt: serverTimestamp(),
              lastAuthTimeSec: authTimeSec || null,
              lastActivityAt: serverTimestamp(),
            }, { merge: true });
          }
        } catch (loginMetaError) {
          console.warn('Login metadata update failed (non-fatal):', loginMetaError?.message || loginMetaError);
        }

        try {
            // Run self-heal in background and do not await it so it cannot block auth readiness.
            // Also treat quota/auth errors as non-fatal warnings.
            (async () => {
              try {
                const idToken = await authClient.currentUser.getIdToken(true);
                const projectId = (authClient && authClient.app && authClient.app.options && authClient.app.options.projectId) || process.env.REACT_APP_FIREBASE_PROJECT_ID || 'myrobot-krinkels';
                const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
                const configuredUrl = process.env.REACT_APP_SELF_HEAL_USERDOC_URL;
                const defaultUrl = `https://${region}-${projectId}.cloudfunctions.net/selfHealUserDoc`;
                const url = configuredUrl || defaultUrl;

                const targetOrigin = new URL(url, window.location.href).origin;
                const sameOrigin = targetOrigin === window.location.origin;
                if (configuredUrl || sameOrigin) {
                  const healRes = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({}),
                  }).catch((e) => {
                    console.debug('selfHealUserDoc fetch failed:', e?.message || e);
                    return null;
                  });
                  if (healRes && typeof healRes.status === 'number' && healRes.status >= 400) {
                    console.warn('selfHealUserDoc non-200:', healRes.status);
                  }
                } else {
                  console.debug('Skipped selfHealUserDoc call to', url, 'to avoid cross-origin CSP reports');
                }
              } catch {
                // Handle specific Firebase auth quota errors gracefully
                const m = String('');
                if (/quota-exceeded/i.test(m) || /auth\/quota-exceeded/i.test(m)) {
                  console.warn('selfHealUserDoc suppressed quota error (non-fatal):', m);
                } else {
                  console.warn('selfHealUserDoc failed (non-fatal):', m);
                }
              }
            })();
        } catch (error) {
          try { console.warn('selfHealUserDoc background start failed (non-fatal):', error?.message || error); } catch { void 0; }
        }
      } else {
        setUser(null);
        setApproved(null);
        setIsAdmin(false);
        setIsGlobalAdmin(false);
        setUserBranchId(null);
        setIsSuperAdmin(false);
        setDemoMode({ enabled: false, showroom: false });
        setUserProfile({});
        setRolesLoaded(false);
        setApprovalResolved(true);
        setUserDocLoading(false);
        setUserDocError(null);
        setBranchAccessResolved(false);
        try { logBootEvent('redirect reason', 'auth-null'); } catch { void 0; }
        setUserRoleCapabilities(buildRoleCapabilities({}));
        logger.info('AUTH', '🚪 User logged out');
        setAuthStatus('unauthenticated');
      }
      // Mark auth as ready promptly; background tasks (self-heal) must not block UI.
      setAuthLoading(false);
      try { logBootEvent('auth ready'); } catch { void 0; }
      try { clearTimeout(initTimeout); } catch { void 0; }
    });
    } catch (err) {
      console.warn('onAuthStateChanged failed to register:', err?.message || err);
      setAuthLoading(false);
      setAuthStatus('error');
      try { clearTimeout(initTimeout); } catch { void 0; }
      unsubscribe = () => {};
    }

    return () => {
      try { clearTimeout(initTimeout); } catch { void 0; }
      try { unsubscribe && typeof unsubscribe === 'function' && unsubscribe(); } catch { void 0; }
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    authProfileSignatureRef.current = null;
    firstUserSnapshotLoggedRef.current = false;
    lastLoggedBranchRef.current = null;
    const userRef = doc(db, 'users', user.uid);
    const syncUserContext = async (userData = {}) => {
      if (cancelled) return;
      const authProfileSignature = JSON.stringify({
        approved: userData.approved, active: userData.active, isActive: userData.isActive,
        blocked: userData.blocked, disabled: userData.disabled, deactivated: userData.deactivated,
        admin: userData.admin, isGlobalAdmin: userData.isGlobalAdmin, isSuperAdmin: userData.isSuperAdmin,
        beheer: userData.beheer, developer: userData.developer, uitvoerder: userData.uitvoerder,
        technician: userData.technician, collega: userData.collega,
        role: userData.role, roles: userData.roles, branchId: userData.branchId,
        branchName: userData.branchName, branchesAllowed: userData.branchesAllowed,
        branchIds: userData.branchIds, vestiging: userData.vestiging, vestigingId: userData.vestigingId,
      });
      setNotificationsEnabled(userData.notificationsEnabled !== false);
      setNotificationPreferences(normalizeNotificationPreferences(userData.notificationPreferences));
      setUserProfile(userData || {});
      if (authProfileSignatureRef.current === authProfileSignature) return;
      authProfileSignatureRef.current = authProfileSignature;
      try { logBootEvent('userDoc fetch start'); } catch { void 0; }
      setUserDocLoading(true);
      setProfileStatus('loading');

      let newBranchId = userData.branchId || null;
      let claimBranchId = null;
      let claimIsSuperAdmin = false;
      let claimIsGlobalAdmin = false;
      let claimIsAdmin = false;

        try {
          const authClient = resolveAuthClient();
          const tokenResult = authClient && authClient.currentUser ? await authClient.currentUser.getIdTokenResult(true) : null;
        const claims = tokenResult?.claims || {};
        claimBranchId = claims.branchId || null;
        claimIsGlobalAdmin = claims.isGlobalAdmin === true || claims.isSuperAdmin === true;

        const claimRoleFlags = deriveRoleFlags({}, claims);
        claimIsSuperAdmin = Boolean(claimRoleFlags.beheer || claimRoleFlags.developer);
        claimIsAdmin = Boolean(claimRoleFlags.uitvoerder || claimIsSuperAdmin);
      } catch (_) {
        void 0;
      }

      if (claimBranchId) newBranchId = claimBranchId;
      newBranchId = canonicalizeVestigingId(newBranchId) || newBranchId;
      if (newBranchId) {
        try {
          newBranchId = (await resolveVestigingDocId(newBranchId)) || newBranchId;
        } catch (_) {
          void 0;
        }
      }

      const roleCapabilities = buildRoleCapabilities(deriveRoleFlags(userData, {}));
      const docIsSuperAdmin = roleCapabilities.canManageAllVestigingen;
      const docIsAdmin = roleCapabilities.canManageAssignedVestiging;
      const docIsTechnician = roleCapabilities.technician;
      const hasOperationalContext = Boolean(
        newBranchId ||
          userData.branchId ||
          userData.branchName ||
          userData.vestigingNaam ||
          userData.branch ||
          docIsTechnician
      );

      const newIsSuperAdmin = claimIsSuperAdmin || docIsSuperAdmin;
      const newIsGlobalAdmin = claimIsGlobalAdmin || userData.isGlobalAdmin === true || userData.isSuperAdmin === true;
      const newIsAdmin = claimIsAdmin || docIsAdmin || newIsSuperAdmin;
      const newApproved =
        userData.approved === true || newIsSuperAdmin || newIsAdmin || hasOperationalContext;

      const incomingDemoMode = userData?.demoMode || {};
      const canUseDemoForUser = Boolean(roleCapabilities.canManageAssignedVestiging || claimIsAdmin || claimIsSuperAdmin);
      const nextDemoMode = {
        enabled: Boolean(canUseDemoForUser && incomingDemoMode.enabled === true),
        showroom: Boolean(canUseDemoForUser && incomingDemoMode.showroom === true),
      };

      if (cancelled) return;

      setApproved(newApproved);
      setUserBranchId(newBranchId || null);
      try { if (newBranchId && lastLoggedBranchRef.current !== newBranchId) logBootEvent('branch ready'); } catch { void 0; }
      setIsSuperAdmin(newIsSuperAdmin);
      setIsGlobalAdmin(newIsGlobalAdmin);
      setIsAdmin(newIsAdmin);
      setUserRoleCapabilities({
        ...roleCapabilities,
        canManageAssignedVestiging: Boolean(roleCapabilities.canManageAssignedVestiging || claimIsAdmin || claimIsSuperAdmin),
        canManageAllVestigingen: Boolean(roleCapabilities.canManageAllVestigingen || claimIsSuperAdmin),
      });
      setDemoMode(nextDemoMode);
      setApprovalResolved(true);
      try { logBootEvent('userDoc fetch success'); } catch { void 0; }
      setUserDocLoading(false);
      setUserDocError(null);
      // set profile status based on approval
      setProfileStatus(newApproved ? 'approved' : 'unapproved');

      // mark branch access resolved for route guards
      setBranchAccessResolved(Boolean(newBranchId));
      if (newBranchId && lastLoggedBranchRef.current !== newBranchId) {
        try { logBootEvent('branch resolved', newBranchId); } catch { void 0; }
        lastLoggedBranchRef.current = newBranchId;
      }
      setRolesLoaded(true);
      logger.logUserContext(user.uid, newIsSuperAdmin, newBranchId || null, user.email);
    };


    // record that we're about to start the realtime subscription for the user
    try { logBootEvent('firestore listeners starting'); } catch { void 0; }

    const unsubscribe = onSnapshot(
      userRef,
      (docSnapshot) => {
        if (!firstUserSnapshotLoggedRef.current) {
          firstUserSnapshotLoggedRef.current = true;
          try { logBootEvent('user doc ready'); } catch { void 0; }
        }
        if (docSnapshot.exists()) {
          void syncUserContext(docSnapshot.data() || {});
        } else {
          if (cancelled) return;
          setApproved(false);
          setUserBranchId(null);
          setIsSuperAdmin(false);
          setIsAdmin(false);
          setIsGlobalAdmin(false);
          setUserProfile({});
          setUserRoleCapabilities(buildRoleCapabilities({}));
          setDemoMode({ enabled: false, showroom: false });
          setApprovalResolved(true);
          setRolesLoaded(true);
          setUserDocLoading(false);
          setProfileStatus('missing');
          try { logBootEvent('userDoc fetch success', 'no-doc'); } catch { void 0; }
        }
      },
      (error) => {
        console.error('Realtime user sync failed:', error);
        setUserDocError(error?.message || String(error));
        setUserDocLoading(false);
        setProfileStatus('error');
        try { logBootEvent('userDoc fetch fail', error?.message || String(error)); } catch { void 0; }
        if (cancelled) return;
        setApprovalResolved(true);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user]);

  // derive overall loading: auth or userDoc
  useEffect(() => {
    setLoading(Boolean(authLoading || userDocLoading));
  }, [authLoading, userDocLoading]);

  // Fallback: if approval/userDoc resolution takes too long (backend errors),
  // allow the app to proceed after a short timeout so pages (e.g. /meldingen)
  // can render with a warning instead of blocking indefinitely.
  useEffect(() => {
    if (!user) return undefined;
    if (approvalResolved) return undefined;
    const APPR_TIMEOUT_MS = Number(process.env.REACT_APP_APPROVAL_TIMEOUT_MS || 4000);
    const id = setTimeout(() => {
      try { console.warn('Approval resolution timeout reached — proceeding with fallback UI (no auto-approve)'); } catch (_) { void 0; }
      // If we still haven't resolved approval, allow app to continue but do NOT auto-approve the user.
      setApprovalResolved(true);
      // Prevent userDoc loading from blocking the overall loading state.
      setUserDocLoading(false);
      setUserDocError('userDoc fetch timeout — proceeding with fallback UI');
      setProfileStatus((s) => (s === 'loading' ? 'error' : s));
      try { logBootEvent('userDoc fetch fail', 'timeout'); } catch (e) { void 0; }
    }, APPR_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [user, approvalResolved, approved]);

  return {
    approvalResolved,
    approved,
    demoMode,
    isAdmin,
    isGlobalAdmin,
    isSuperAdmin,
    loading,
    authLoading,
    authStatus,
    profileStatus,
    userDocLoading,
    userDocError,
    branchAccessResolved,
    notificationPreferences,
    notificationsEnabled,
    rolesLoaded,
    setDemoMode,
    setNotificationPreferences,
    setNotificationsEnabled,
    setUserBranchId,
    user,
    userProfile,
    userBranchId,
    userRoleCapabilities,
  };
}
